"use client";

import Link from "next/link";
import Image from "next/image";
import { CalendarIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { getDayAndMonth } from "@utils/getDayAndMonth";
import { useResizeObserver, useWindowSize } from "@utils/hooks";
import { motion } from "framer-motion";
import { useState } from "react";
import { joinClassNames } from "@utils/helpers";
import { Text, Badge } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import { useTranslations } from "next-intl";
import { MegaphoneIcon } from "@heroicons/react/24/solid";
import {
  formatGTMSelectItemEvent,
  sendIticketGTMTrackingEvent,
} from "@utils/analyticsUtils";

interface Props {
  event: {
    Id?: number;
    Name: string;
    Url: string;
    Byline: string;
    Region: string | null;
    Date: { Start: string | null; End: string | null; Range: string | null };
    Images: { BannerSmall: string; DefaultSmall: string; Portrait: string };
  };
  imageIsPriority?: boolean;
  showEventDateRangesInline?: boolean;
  showEventRegions?: boolean;
  /** When set, fires a GA4 select_item event (tagged with this list name) on click. */
  itemListName?: string;
}

export default function EventLink({
  event,
  imageIsPriority = false,
  showEventDateRangesInline,
  showEventRegions,
  itemListName,
}: Props) {
  const windowSize = useWindowSize();
  const [hoveringDate, setHoveringDate] = useState(false);
  const [dateRangeVisible, setDateRangeVisible] = useState(false);
  const isShopsite = useIsShopsite();
  const t = useTranslations("Utils");
  const { ref, clientHeight } = useResizeObserver();

  const dateRangeText = event.Date.Range
    ? event.Date.Range
    : event.Date.Start
    ? `${getDayAndMonth(
        event.Date.Start,
        event.Date.End &&
          event.Date.Start.split("T")[0] !== event.Date.End.split("T")[0]
          ? {
              day: "numeric",
              month: "short",
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
          : {
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
      )}
  ${
    event.Date.End &&
    event.Date.Start.split("T")[0] !== event.Date.End.split("T")[0]
      ? ` ${t("to")} ${getDayAndMonth(event.Date.End, {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })}`
      : ""
  }`
    : null;

  const imageSrc =
    windowSize.width && windowSize.width > 640
      ? event.Images.Portrait.trim()
      : event.Images.BannerSmall.trim();

  return (
    <Link
      data-testid={`event-${event.Url}`}
      href={event.Url || "#"}
      onClick={() => {
        if (itemListName && event.Id) {
          sendIticketGTMTrackingEvent(
            formatGTMSelectItemEvent(event.Id, event.Name, itemListName)
          );
        }
      }}
      onMouseEnter={(e) => {
        e.preventDefault();
        setHoveringDate(true);
        setDateRangeVisible(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();
        setHoveringDate(false);
        setTimeout(() => setDateRangeVisible(false), 200);
      }}
      className={joinClassNames(
        "relative group flex flex-col gap-1 p-2 w-full h-fit transition-colors ease-in-out duration-500",
        isShopsite
          ? "sm:hover:bg-indigo-50/75 rounded-2xl"
          : "sm:hover:bg-[color:var(--accent-a2)] rounded-[var(--radius-4)]"
      )}
    >
      <div className="relative">
        {imageSrc ? (
          <Image
            src={
              windowSize.width && windowSize.width <= 640
                ? event.Images.BannerSmall.trim()
                : event.Images.Portrait.trim()
            }
            alt={event.Name}
            width={192}
            height={windowSize.width && windowSize.width <= 640 ? 96 : 224}
            priority={imageIsPriority}
            className={joinClassNames(
              "object-cover w-full aspect-[2/1] sm:aspect-[6/7]",
              isShopsite ? "rounded-xl" : "rounded-[var(--radius-3)]"
            )}
          />
        ) : (
          <div
            className={joinClassNames(
              "flex items-center justify-center w-full aspect-[2/1] sm:aspect-[6/7]",
              isShopsite
                ? "bg-indigo-50 rounded-xl"
                : "bg-[color:var(--accent-a2)] rounded-[var(--radius-3)]"
            )}
          >
            <MegaphoneIcon
              className={joinClassNames(
                "w-1/2 h-1/2",
                isShopsite ? "text-indigo-800" : "text-[color:var(--accent-9)]"
              )}
            />
          </div>
        )}
        {dateRangeText && !showEventDateRangesInline ? (
          <>
            {/* invisible span to measure height of the text */}
            <span
              className="invisible text-sm max-w-[calc(100%-48px)] absolute"
              ref={ref}
            >
              {dateRangeText}
            </span>
            <div
              className={joinClassNames(
                "hidden sm:flex items-center absolute bottom-2 right-2 ml-2 p-1 text-sm overflow-clip",
                dateRangeVisible ? "gap-1" : "",
                isShopsite
                  ? "bg-indigo-100 text-indigo-800 rounded-lg"
                  : "bg-[color:var(--accent-3)] text-[color:var(--accent-a11)] backdrop-blur rounded-[var(--radius-2)]"
              )}
            >
              <div className="flex items-center justify-center w-5 h-5 shrink-0">
                <CalendarIcon className="h-4 w-4 shrink-0" />
              </div>
              <motion.span
                initial={{
                  width: 0,
                  height: "20px",
                  opacity: 0,
                }}
                animate={
                  hoveringDate
                    ? {
                        width: "auto",
                        height: clientHeight,
                        opacity: 1,
                        transition: { opacity: { delay: 0.3 } },
                      }
                    : {
                        width: 0,
                        height: "20px",
                        opacity: 0,
                        transition: {
                          width: { delay: 0.2 },
                          height: { delay: 0.2 },
                        },
                      }
                }
                className="pointer-events-none will-change-transform"
              >
                {dateRangeText}
              </motion.span>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      <Text
        as="p"
        size="4"
        weight="bold"
        whitelabelClasses="sm:group-hover:text-[color:var(--accent-11)] transition-colors ease-in-out duration-500"
        shopsiteClasses="text-lg font-semibold text-black"
      >
        {event.Name}
      </Text>
      <Text as="p" color="gray" shopsiteClasses="text-gray-500">
        {event.Byline}
      </Text>
      {dateRangeText ? (
        <Badge
          size="2"
          sharedClasses={joinClassNames(
            "w-fit max-w-full",
            showEventDateRangesInline ? "sm:whitespace-normal" : "sm:hidden"
          )}
          shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg"
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="max-w-[calc(100%-18px)] max-sm:truncate">
            {dateRangeText}
          </span>
        </Badge>
      ) : (
        <></>
      )}
      {event.Region && showEventRegions ? (
        <Badge
          color="gray"
          size="2"
          sharedClasses="w-fit"
          shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg max-w-full"
        >
          <MapPinIcon className="h-4 w-4" />
          <span className="max-w-[calc(100%-18px)] truncate">
            {event.Region}
          </span>
        </Badge>
      ) : (
        <></>
      )}
    </Link>
  );
}
